import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../constants/api';
import { useSidebar } from './SidebarContext';
import supabase from '../utils/supabase';
import useAdminDocumentMap from '../hooks/useAdminDocumentMap';
import { TemplateInfo, LevelType, TabCategory, TabItem, Subcategory, InlineEdit, DocumentMapCategory } from '../types/AdminTypes';

// ── Types ──────────────────────────────────────────────────────────────────

interface AdminUser {
  id: string;
  email: string;
  name?: string;
}

interface AdminContextValue {
  // Auth
  isAdmin: boolean;
  adminUser: AdminUser | null;
  adminToken: string | null;
  loading: boolean;
  logout: () => void;

  // Document map fetch
  documentMap: DocumentMapCategory[];
  documentMapLoading: boolean;
  documentMapError: string | null;
  fetchDocumentMap: () => Promise<void>;

  // Document map UI state
  level: LevelType;
  setLevel: (l: LevelType) => void;
  filteredCats: DocumentMapCategory[];
  selectedCat: DocumentMapCategory | null;
  selectedCatId: string | null;
  selectedSubcat: Subcategory | null;
  selectCategory: (id: string) => void;
  drillIntoSubcat: (subcat: Subcategory) => void;
  exitSubcat: () => void;
  catTab: TabCategory;
  setCatTab: (t: TabCategory) => void;
  subcatTab: TabItem;
  setSubcatTab: (t: TabItem) => void;
  catEdit: InlineEdit;
  docTypeEdit: InlineEdit;
  fieldTypeEdit: InlineEdit;

  // Category CRUD
  addCategory: (name: string) => Promise<void>;
  renameCategory: (id: string) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;

  // Subcategory CRUD
  addSubcategory: (name: string) => Promise<void>;
  renameSubcategory: (id: string, name: string) => Promise<void>;
  deleteSubcategory: (id: string) => Promise<void>;

  // Document type CRUD
  addDocumentType: (name: string, recurrence: string, multi_allowed: string, viewingRole: string, edittingRole: string, catId: string, subcatId?: string) => Promise<void>;
  updateDocumentType: (row: { id: string; name: string; recurrence: string; multi_uploads: boolean, viewingRole: string, edittingRole: string}, catId: string, subcatId?: string) => Promise<void>;
  deleteDocumentType: (id: string, catId: string, subcatId?: string) => Promise<void>;

  // Field type CRUD
  addFieldType: (name: string, value_type: string, required: boolean, viewingRole: string, edittingRole: string, catId: string, subcatId?: string) => Promise<void>;
  updateFieldType: (row: { id: string, name: string, value_type: string, required: boolean, viewingRole: string, edittingRole: string} , catId: string, subcatId?: string) => Promise<void>;
  deleteFieldType: (id: string, catId: string, subcatId?: string) => Promise<void>;

  // Document type template CRUD
  templatesMap: Record<string, TemplateInfo>;
  uploadTemplate: (file: File, documentTypeId: string, categoryId: string, subcategoryId?: string) => Promise<void>;
  downloadAdminTemplate: (templateId: string) => Promise<void>;
  deleteTemplate: (templateId: string, documentTypeId: string) => Promise<void>;
}

const noop = () => {};
const noopAsync = async () => {};
const emptyEdit: InlineEdit = { editingId: null, editValue: '', setEditValue: noop, start: noop, cancel: noop };

const AdminContext = createContext<AdminContextValue>({
  isAdmin: false,
  adminUser: null,
  adminToken: null,
  loading: true,
  logout: noop,
  documentMap: [],
  documentMapLoading: false,
  documentMapError: null,
  fetchDocumentMap: noopAsync,
  level: 'employee',
  setLevel: noop,
  filteredCats: [],
  selectedCat: null,
  selectedCatId: null,
  selectedSubcat: null,
  selectCategory: noop,
  drillIntoSubcat: noop,
  exitSubcat: noop,
  catTab: 'subcategories',
  setCatTab: noop,
  subcatTab: 'documentTypes',
  setSubcatTab: noop,
  catEdit: emptyEdit,
  docTypeEdit: emptyEdit,
  fieldTypeEdit: emptyEdit,
  addCategory: noopAsync,
  renameCategory: noopAsync,
  deleteCategory: noopAsync,
  addSubcategory: noopAsync,
  renameSubcategory: noopAsync,
  deleteSubcategory: noopAsync,
  addDocumentType: noopAsync,
  updateDocumentType: noopAsync,
  deleteDocumentType: noopAsync,
  addFieldType: noopAsync,
  updateFieldType: noopAsync,
  deleteFieldType: noopAsync,
  templatesMap: {},
  uploadTemplate: noopAsync,
  downloadAdminTemplate: noopAsync,
  deleteTemplate: noopAsync,
});

export const useAdmin = () => useContext(AdminContext);

const ADMIN_SIDEBAR_ITEMS = [
  { id: 'admin-dashboard', label: 'Dashboard', to: '/admin', icon: '📟' },
  { id: 'admin-document-map', label: 'Document Map', to: '/admin/document-map', icon: '🗺' },
  { id: 'admin-legislation', label: 'Legislation', to: '/admin/legislation', icon: '⚖️' },
  { id: 'admin-app-updates', label: 'App Updates', to: '/admin/app-updates', icon: '📦' },
  { id: 'admin-guides', label: 'Guides', to: '/admin/guides', icon: '📖' },
  { id: 'admin-resources', label: 'Resources', to: '/admin/resources', icon: '📚' },
  { id: 'admin-news', label: 'News', to: '/admin/news', icon: '📰' },
];

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [adminToken, setAdminToken] = useState<string | null>(() => sessionStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  const docMap = useAdminDocumentMap(adminToken);

  const { setItems, setFooterItems, setActiveItem } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  const clearSidebar = useCallback(() => {
    setItems([]);
    setFooterItems([]);
    setActiveItem(null);
  }, [setItems, setFooterItems, setActiveItem]);

  const doLogout = useCallback(() => {
    sessionStorage.removeItem('adminToken');
    setAdminToken(null);
    setAdminUser(null);
    docMap.resetDocumentMap();
    clearSidebar();
    supabase.auth.signOut();
    navigate('/admin/login');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSidebar, navigate]);

  const setupSidebar = useCallback((pathname: string) => {
    setItems(ADMIN_SIDEBAR_ITEMS as any);
    setFooterItems([{ id: 'admin-logout', label: 'Logout', onClick: () => doLogout() }] as any);
    const active = ADMIN_SIDEBAR_ITEMS.find(
      item => pathname === item.to || (item.to !== '/admin' && pathname.startsWith(item.to))
    );
    setActiveItem(active ? active.id : 'admin-dashboard');
  }, [setItems, setFooterItems, setActiveItem, doLogout]);

  // Verify token once on mount
  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) { setLoading(false); return; }
    let mounted = true;
    axios
      .get(`${API_BASE_URL}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (mounted) { setAdminToken(token); setAdminUser(res.data.user); } })
      .catch(() => { if (mounted) { sessionStorage.removeItem('adminToken'); setAdminToken(null); } })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply sidebar on every admin page navigation; trigger document map fetch
  useEffect(() => {
    if (!adminUser) return;
    if (location.pathname.startsWith('/admin') && location.pathname !== '/admin/login') {
      setupSidebar(location.pathname);
      docMap.fetchDocumentMap();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, adminUser, setupSidebar]);

  return (
    <AdminContext.Provider
      value={{
        isAdmin: Boolean(adminToken && adminUser),
        adminUser,
        adminToken,
        loading,
        logout: doLogout,
        documentMap: docMap.documentMap,
        documentMapLoading: docMap.documentMapLoading,
        documentMapError: docMap.documentMapError,
        fetchDocumentMap: docMap.fetchDocumentMap,
        level: docMap.level,
        setLevel: docMap.setLevel,
        filteredCats: docMap.filteredCats,
        selectedCat: docMap.selectedCat,
        selectedCatId: docMap.selectedCatId,
        selectedSubcat: docMap.selectedSubcat,
        selectCategory: docMap.selectCategory,
        drillIntoSubcat: docMap.drillIntoSubcat,
        exitSubcat: docMap.exitSubcat,
        catTab: docMap.catTab,
        setCatTab: docMap.setCatTab,
        subcatTab: docMap.subcatTab,
        setSubcatTab: docMap.setSubcatTab,
        catEdit: docMap.catEdit,
        docTypeEdit: docMap.docTypeEdit,
        fieldTypeEdit: docMap.fieldTypeEdit,
        addCategory: docMap.addCategory,
        renameCategory: docMap.renameCategory,
        deleteCategory: docMap.deleteCategory,
        addSubcategory: docMap.addSubcategory,
        renameSubcategory: docMap.renameSubcategory,
        deleteSubcategory: docMap.deleteSubcategory,
        addDocumentType: docMap.addDocumentType,
        updateDocumentType: docMap.updateDocumentType,
        deleteDocumentType: docMap.deleteDocumentType,
        addFieldType: docMap.addFieldType,
        updateFieldType: docMap.updateFieldType,
        deleteFieldType: docMap.deleteFieldType,
        templatesMap: docMap.templatesMap,
        uploadTemplate: docMap.uploadTemplate,
        downloadAdminTemplate: docMap.downloadAdminTemplate,
        deleteTemplate: docMap.deleteTemplate,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
